import React, { FC, useCallback, useMemo, useState } from 'react';
import DataGrid, { Column, RenderEditCellProps, textEditor } from 'react-data-grid';
import 'react-data-grid/lib/styles.css';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Select, Tooltip } from 'antd';
import { ImportField, ImportRow } from './ImportGridTypes';
import { validateRows } from './validateRows';

interface EditableImportGridProps {
	fields: ImportField[];
	rows: ImportRow[];
	onRowsChange: (rows: ImportRow[]) => void;
	recordHook?: (record: any) => any;
}

const SelectEditor = ({ row, column, onRowChange, onClose, options }: any) => (
	<Select
		autoFocus
		defaultOpen
		showSearch
		className="h-full w-full"
		bordered={false}
		value={row[column.key] !== undefined && row[column.key] !== '' ? row[column.key] : undefined}
		placeholder="Search..."
		filterOption={(input, option) =>
			String((option && option.children) || '')
				.toLowerCase()
				.includes(input.toLowerCase())
		}
		onChange={(val) => onRowChange({ ...row, [column.key]: val }, true)}
		onBlur={() => onClose(true)}
		dropdownStyle={{ zIndex: 2000 }}
	>
		{options.map((option) => (
			<Select.Option key={option.value} value={option.value}>
				{option.label}
			</Select.Option>
		))}
	</Select>
);

const NumberEditor = ({ row, column, onRowChange, onClose }: RenderEditCellProps<ImportRow>) => (
	<input
		autoFocus
		type="number"
		className="rdg-text-editor h-full w-full border-none outline-none"
		value={row[column.key] !== undefined ? row[column.key] : ''}
		onChange={(event) => {
			const val = event.target.value;
			onRowChange({ ...row, [column.key]: val === '' ? '' : Number(val) });
		}}
		onBlur={() => onClose(true)}
	/>
);

const CheckboxFormatter = ({ row, column, onRowChange }: any) => (
	<input
		type="checkbox"
		checked={!!row[column.key]}
		onChange={(event) => onRowChange({ ...row, [column.key]: event.target.checked })}
		className="m-auto block"
	/>
);

const CellWithErrorTooltip = ({
	field,
	row,
	children,
}: {
	field: ImportField;
	row: ImportRow;
	children: React.ReactNode;
}) => {
	const errs = row._errors && row._errors[field.key];
	if (errs && errs.length > 0) {
		return (
			<Tooltip title={errs.join(', ')} color="#ff4d4f">
				<div className="h-full w-full">{children}</div>
			</Tooltip>
		);
	}
	return <>{children}</>;
};

const rowHasErrors = (row: ImportRow): boolean => {
	if (!row._errors) return false;
	return Object.values(row._errors).some((errors) => errors && errors.length > 0);
};

const EditableImportGrid: FC<EditableImportGridProps> = ({
	fields,
	rows,
	onRowsChange,
	recordHook,
}) => {
	const [showErrorsOnly, setShowErrorsOnly] = useState(false);

	const runValidation = useCallback(
		(updatedRows: ImportRow[]): ImportRow[] => validateRows(updatedRows, fields, recordHook),
		[fields, recordHook]
	);

	const handleRowsChange = useCallback(
		(newRows: ImportRow[]) => {
			if (!showErrorsOnly) {
				onRowsChange(runValidation(newRows));
				return;
			}
			// react-data-grid hands us back the FILTERED set with edits applied.
			// Merge those edits back into the full row list by `_rowId` so hidden
			// rows aren't dropped.
			const updatesById = new Map<number, ImportRow>();
			newRows.forEach((row) => updatesById.set(row._rowId, row));
			const merged = rows.map((row) => updatesById.get(row._rowId) || row);
			onRowsChange(runValidation(merged));
		},
		[onRowsChange, runValidation, rows, showErrorsOnly]
	);

	const handleToggleShowErrorsOnly = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setShowErrorsOnly(event.target.checked);
	}, []);

	const handleAddRow = useCallback(() => {
		const newRow: ImportRow = {
			_rowId: Date.now() + Math.random(),
			_errors: {},
		};
		fields.forEach((field) => {
			newRow[field.key] = field.type === 'boolean' ? false : '';
		});
		const updated = [...rows, newRow];
		onRowsChange(runValidation(updated));
	}, [fields, rows, onRowsChange, runValidation]);

	const handleDeleteRow = useCallback(
		(rowId: number) => {
			const updated = rows.filter((row) => row._rowId !== rowId);
			onRowsChange(runValidation(updated));
		},
		[rows, onRowsChange, runValidation]
	);

	const originalIndexById = useMemo(() => {
		const map = new Map<number, number>();
		rows.forEach((row, index) => map.set(row._rowId, index));
		return map;
	}, [rows]);

	const columns: Column<ImportRow>[] = useMemo(() => {
		const snoCol: Column<ImportRow> = {
			key: '_sno',
			name: 'S.No.',
			width: 60,
			resizable: false,
			frozen: true,
			cellClass: 'import-grid-cell-readonly',
			renderCell: ({ row }) => {
				const originalIndex = originalIndexById.get(row._rowId);
				return <>{(originalIndex !== undefined ? originalIndex : 0) + 1}</>;
			},
		};

		const dataCols: Column<ImportRow>[] = fields.map((field) => {
			const base: Column<ImportRow> = {
				key: field.key,
				name: field.label,
				resizable: true,
				cellClass: (row) => {
					const errs = row._errors && row._errors[field.key];
					return errs && errs.length > 0 ? 'import-grid-cell-error' : undefined;
				},
			};

			if (field.readonly) {
				return {
					...base,
					editable: false,
					cellClass: (row) => {
						const errClass =
							row._errors && row._errors[field.key] && row._errors[field.key].length > 0
								? 'import-grid-cell-error'
								: '';
						return ('import-grid-cell-readonly ' + errClass).trim();
					},
					renderCell: ({ row }) => (
						<CellWithErrorTooltip field={field} row={row}>
							{row[field.key] || ''}
						</CellWithErrorTooltip>
					),
				};
			}

			if (field.type === 'enum' && field.config && field.config.options) {
				const options = field.config.options;
				return {
					...base,
					editable: true,
					renderEditCell: (props) => <SelectEditor {...props} options={options} />,
					renderCell: ({ row }) => {
						const val = row[field.key];
						const matched = options.find((option) => String(option.value) === String(val));
						return (
							<CellWithErrorTooltip field={field} row={row}>
								{matched ? matched.label : val || ''}
							</CellWithErrorTooltip>
						);
					},
				};
			}

			if (field.type === 'boolean') {
				return {
					...base,
					renderCell: (props) => (
						<CellWithErrorTooltip field={field} row={props.row}>
							<CheckboxFormatter {...props} />
						</CellWithErrorTooltip>
					),
				};
			}

			if (field.type === 'number') {
				return {
					...base,
					editable: true,
					renderEditCell: NumberEditor,
					renderCell: ({ row }) => (
						<CellWithErrorTooltip field={field} row={row}>
							{row[field.key] !== undefined && row[field.key] !== null ? row[field.key] : ''}
						</CellWithErrorTooltip>
					),
				};
			}

			return {
				...base,
				editable: true,
				renderEditCell: textEditor,
				renderCell: ({ row }) => (
					<CellWithErrorTooltip field={field} row={row}>
						{row[field.key] || ''}
					</CellWithErrorTooltip>
				),
			};
		});

		const deleteCol: Column<ImportRow> = {
			key: '_delete',
			name: '',
			width: 40,
			resizable: false,
			renderCell: ({ row }) => (
				<button
					onClick={() => handleDeleteRow(row._rowId)}
					className="h-full w-full cursor-pointer border-none bg-transparent text-character-danger"
					title="Delete row"
				>
					<DeleteOutlined />
				</button>
			),
		};

		return [snoCol, ...dataCols, deleteCol];
	}, [fields, handleDeleteRow, originalIndexById]);

	const handleCellClick = useCallback((args) => {
		// The delete-button cell removes the row in its own onClick. Scheduling
		// selectCell on a row that's about to disappear hits a stale cell ref.
		if (args.column.key === '_delete') return;
		setTimeout(() => args.selectCell(true), 0);
	}, []);

	const errorRowCount = useMemo(
		() => rows.reduce((count, row) => count + (rowHasErrors(row) ? 1 : 0), 0),
		[rows]
	);

	// Treat the "errors only" filter as inactive whenever there are no errors
	// to show, so the grid never goes silently empty after the user finishes
	// fixing them. Computed during render — not stored — so toggling back on
	// is instantaneous when new errors appear, and there is no frame where
	// `displayRows` is an empty filtered set waiting on an effect to reset.
	const effectiveShowErrorsOnly = showErrorsOnly && errorRowCount > 0;

	const displayRows = useMemo(
		() => (effectiveShowErrorsOnly ? rows.filter(rowHasErrors) : rows),
		[rows, effectiveShowErrorsOnly]
	);

	return (
		<div>
			<div className="mb-2 flex items-center justify-between">
				<div className="flex items-center">
					{errorRowCount > 0 && (
						<span className="text-character-danger">
							{errorRowCount} row{errorRowCount !== 1 ? 's' : ''} with errors
						</span>
					)}
					<span className="ml-3 text-[#999]">
						{effectiveShowErrorsOnly
							? `${displayRows.length} of ${rows.length} row${rows.length !== 1 ? 's' : ''}`
							: `${rows.length} row${rows.length !== 1 ? 's' : ''}`}
					</span>
					<label
						className={`ml-3 flex items-center gap-1 whitespace-nowrap ${
							errorRowCount > 0 ? 'cursor-pointer' : 'cursor-not-allowed text-[#bbb]'
						}`}
						title={errorRowCount > 0 ? 'Filter to rows with errors only' : 'No rows have errors'}
					>
						<input
							type="checkbox"
							checked={effectiveShowErrorsOnly}
							disabled={errorRowCount === 0}
							onChange={handleToggleShowErrorsOnly}
						/>
						Show errors only
					</label>
				</div>
				<button
					onClick={handleAddRow}
					className="flex cursor-pointer items-center gap-1 rounded border border-owgray bg-white px-3 py-1"
				>
					<PlusOutlined /> Add Row
				</button>
			</div>
			<DataGrid
				columns={columns}
				rows={displayRows}
				rowKeyGetter={(row) => row._rowId}
				onRowsChange={handleRowsChange}
				onCellClick={handleCellClick}
				className="import-grid max-h-[600px] min-h-[400px]"
			/>
			<style>{`
				.import-grid {
					--rdg-background-color: #fff;
					--rdg-header-background-color: #fafafa;
					--rdg-row-hover-background-color: #f5f5f5;
					--rdg-color: #262626;
					--rdg-font-size: 14px;
					--rdg-color-scheme: light;
				}
				/* Prod CSS minifier (cssnano mergeRules) merges react-data-grid's
				   resize-handle rule with unrelated header-cell rules, leaking
				   inline-size: 10px and position: absolute onto the header cell
				   itself and clipping header text to a single character. Reset
				   on the header cell; the resize handle (direct child) keeps
				   its absolute 10px sizing. */
				.import-grid [role="columnheader"] {
					inline-size: auto;
					position: sticky;
					cursor: default;
					inset-block-start: 0;
					inset-inline-end: auto;
					inset-block-end: auto;
				}
				.import-grid ::-webkit-scrollbar {
					width: 8px;
					height: 8px;
				}
				.import-grid ::-webkit-scrollbar-track {
					background: #f1f1f1;
				}
				.import-grid ::-webkit-scrollbar-thumb {
					background: #c1c1c1;
					border-radius: 4px;
				}
				.import-grid ::-webkit-scrollbar-thumb:hover {
					background: #a8a8a8;
				}
				.import-grid-cell-readonly {
					background-color: #f5f5f5 !important;
					color: #999 !important;
					cursor: not-allowed;
				}
				.import-grid-cell-error {
					background-color: #fff2f0 !important;
					border-color: #ff4d4f !important;
				}
			`}</style>
		</div>
	);
};

export default EditableImportGrid;
